import { computed, ref } from 'vue';

import { ProjectSettings } from '../context';
import { PWA_DISPLAY_MODES, PWA_RELATED_WEB_APP_PLATFORM } from './constants';
import { PwaInstructionsPlatform, type BeforeInstallPromptEvent, type PwaService } from './types';

const createPwaService = (): PwaService => {
  const deferredInstallPrompt = ref<BeforeInstallPromptEvent | null>(null);
  const isRunning = ref<boolean>(false);
  const isManualInstallAvailable = ref<boolean>(false);
  const isRelatedAppInstalled = ref<boolean>(false);
  let isInitialized: boolean = false;

  const isAppleMobileDevice = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isIPadOs = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    return /iphone|ipad|ipod/.test(userAgent) || isIPadOs;
  };

  const isLaunchedAsPwa = (): boolean =>
    PWA_DISPLAY_MODES.some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches) ||
    window.navigator.standalone === true ||
    document.referrer.startsWith('android-app://');

  const canShowManualInstallInstructions = (): boolean =>
    isAppleMobileDevice() && !/crios|fxios|edgios/.test(navigator.userAgent.toLowerCase());

  const updateRunningState = (): void => {
    isRunning.value = isLaunchedAsPwa();
  };

  const saveInstallPrompt = (event: Event): void => {
    event.preventDefault();
    deferredInstallPrompt.value = event as BeforeInstallPromptEvent;
  };

  const handleInstallation = (): void => {
    deferredInstallPrompt.value = null;
    isRunning.value = true;
  };

  const updateRelatedAppInstallationState = async (): Promise<void> => {
    if (!navigator.getInstalledRelatedApps) {
      return;
    }

    try {
      const relatedApps = await navigator.getInstalledRelatedApps();
      isRelatedAppInstalled.value = relatedApps.some(
        ({ platform }) => platform === PWA_RELATED_WEB_APP_PLATFORM,
      );
    } catch {
      isRelatedAppInstalled.value = false;
    }
  };

  const init = (): void => {
    if (isInitialized || typeof window === 'undefined') {
      return;
    }

    isInitialized = true;
    updateRunningState();
    isManualInstallAvailable.value = canShowManualInstallInstructions();
    void updateRelatedAppInstallationState();
    PWA_DISPLAY_MODES.forEach((mode) => {
      window.matchMedia(`(display-mode: ${mode})`).addEventListener('change', updateRunningState);
    });

    window.addEventListener('beforeinstallprompt', saveInstallPrompt);
    window.addEventListener('appinstalled', handleInstallation);
  };

  const requestInstallation = async (): Promise<boolean> => {
    const installPrompt = deferredInstallPrompt.value;
    if (!installPrompt) {
      return false;
    }

    await installPrompt.prompt();
    await installPrompt.userChoice;
    deferredInstallPrompt.value = null;

    return true;
  };

  const isInstallActionVisible = computed<boolean>(
    () =>
      ProjectSettings.pwa.enabled &&
      !isRunning.value &&
      !isRelatedAppInstalled.value &&
      (deferredInstallPrompt.value !== null || isManualInstallAvailable.value),
  );

  const instructionsPlatform = computed<PwaInstructionsPlatform>(() => {
    if (isAppleMobileDevice()) {
      return PwaInstructionsPlatform.IOS;
    }

    if (/android/.test(navigator.userAgent.toLowerCase())) {
      return PwaInstructionsPlatform.ANDROID;
    }

    return PwaInstructionsPlatform.OTHER;
  });

  return {
    isInstallActionVisible,
    instructionsPlatform,
    init,
    requestInstallation,
  };
};

const pwaService = createPwaService();

export { pwaService };
